import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import AvatarEditor from "react-avatar-editor";
import "react-datepicker/dist/react-datepicker.css";
import AuthService from "../../services/authService";
import AdminChildSelect from "./AdminChildSelect/AdminChildSelect";
import Container from "../../components/Common/Container/Container";
import AdminChildrenService from "../../services/adminChildrenService";
import { AdminChild as AdminChildEntity } from "../../types/child";
import {
  AdminChildAvatar,
  AdminChildAvatarButton,
  AdminChildAvatarEditor,
  AdminChildAvatarImage,
  AdminChildAvatarScale,
  AdminChildAvatarTitle,
  AdminChildBackButton,
  AdminChildBlock,
  AdminChildButton,
  AdminChildButtons,
  AdminChildContent,
  AdminChildError,
  AdminChildImage,
  AdminChildImageAdd,
  AdminChildImageBlock,
  AdminChildImageDelete,
  AdminChildImages,
  AdminChildImagesBody,
  AdminChildImagesTitle,
  AdminChildInfo,
  AdminChildInfoContentItem,
  AdminChildInfoContentItemDatePicker,
  AdminChildInfoContentItemInput,
  AdminChildInfoContentItemText,
  AdminChildLoading,
  AdminChildLoadingCircle,
  AdminChildLegalBlock,
  AdminChildLegalButton,
  AdminChildLegalCheckbox,
  AdminChildLegalCheckboxInput,
  AdminChildLegalLink,
  AdminChildLegalText,
  AdminChildLegalTitle,
} from "./AdminChildStyles";

const LEGAL_ERROR = "Для регистрации анкеты необходимо принять оферту и дать согласие на обработку персональных данных.";

const AdminChild = () => {
  const params = useParams();
  const navigate = useNavigate();

  const editor = useRef<AvatarEditor>(null);
  const [scale, setScale] = useState(1);

  const [user, setUser] = useState<Omit<AdminChildEntity, "login" | "password"> | null>(null);
  const [child, setChild] = useState<AdminChildEntity | null>(null);

  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [sex, setSex] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [height, setHeight] = useState("");
  const [hairColor, setHairColor] = useState("");
  const [eyeColor, setEyeColor] = useState("");
  const [shoesSize, setShoesSize] = useState("");
  const [city, setCity] = useState("");
  const [video, setVideo] = useState("");
  const [secondVideo, setSecondVideo] = useState("");
  const [offerAccepted, setOfferAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [parentConsentAccepted, setParentConsentAccepted] = useState(false);
  const [legalError, setLegalError] = useState("");

  const isLegalAccepted = !!child?.offerAccepted && !!child?.privacyAccepted && !!child?.parentConsentAccepted;
  const canEditChild = !!child && isLegalAccepted;

  function requireLegalConsent() {
    if (canEditChild) {
      return true;
    }

    setLegalError(LEGAL_ERROR);
    return false;
  }

  async function getData() {
    const childId = params.id ?? -1;

    const user = AuthService.getUser();
    const { data: newChild, status } = await AdminChildrenService.getChild(childId);

    if (!newChild || !user || status === "error") {
      AuthService.removeAuth();
      navigate("/admin/login");
      return;
    }

    if (!child) {
      setName(newChild.name);
      setSurname(newChild.surname);
      setSpecialization(newChild.specialization);
      setSex(newChild.sex);
      setBirthDate(newChild.birthDate ? moment(newChild.birthDate).format(`YYYY-MM-DD`) : "");
      setHeight(newChild.height as any);
      setHairColor(newChild.hairColor);
      setEyeColor(newChild.eyeColor);
      setShoesSize(newChild.shoesSize);
      setCity(newChild.city);
      setVideo(newChild.video);
      setSecondVideo(newChild.secondVideo);
    }

    setUser(user);
    setChild(newChild);

    setIsLoading(false);
  }

  useEffect(() => {
    getData();
  }, []);

  const validateImage = (file: File) => {
    if (!file || file.size > 30 * 1024 * 1024) {
      return false;
    }

    return true;
  };

  const resizeImage = (file: File, maxSize: number): Promise<Blob> => {
    var reader = new FileReader();
    var image = new Image();

    var canvas = document.createElement("canvas");

    const dataURItoBlob = (dataURI: string) => {
      const bytes = dataURI.split(",")[0].indexOf("base64") >= 0 ? atob(dataURI.split(",")[1]) : unescape(dataURI.split(",")[1]);
      const mime = dataURI.split(",")[0].split(":")[1].split(";")[0];
      const max = bytes.length;
      const ia = new Uint8Array(max);
      for (var i = 0; i < max; i++) ia[i] = bytes.charCodeAt(i);
      return new Blob([ia], { type: mime });
    };

    const resize = () => {
      let width = image.width;
      let height = image.height;
      if (width > height) {
        if (width > maxSize) {
          height *= maxSize / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width *= maxSize / height;
          height = maxSize;
        }
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d")?.drawImage(image, 0, 0, width, height);
      const dataUrl = canvas.toDataURL("image/jpeg");
      return dataURItoBlob(dataUrl);
    };

    return new Promise(function (ok, no) {
      if (!file.type.match(/image.*/)) {
        no(new Error("Not an image"));
        return;
      }

      reader.onload = function (readerEvent) {
        image.onload = function () {
          return ok(resize());
        };
        image.src = (readerEvent.target?.result ?? "") as string;
      };

      reader.readAsDataURL(file);
    });
  };

  async function onAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();

    if (!requireLegalConsent()) {
      e.currentTarget.value = "";
      return;
    }

    const files = (e.currentTarget as any).files;
    const file = files[0];

    const isImageValid = validateImage(file);

    if (!isImageValid || !child) {
      e.currentTarget.value = "";
      return;
    }

    setIsLoading(true);

    const formData = new FormData();

    const correctImage = (await resizeImage(file, 1024)) as any;

    formData.append("avatar", correctImage, "avatar.jpg");

    const res = await AdminChildrenService.changeAvatar(child.id, formData);
    if (res.status === "success") {
      getData();
    }
  }

  async function onAvatarResize() {
    if (!requireLegalConsent()) {
      return;
    }

    if (!editor.current || !child) {
      return;
    }

    const canvas = editor.current.getImageScaledToCanvas();

    setIsLoading(true);

    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }

      let file = new File([blob], "avatar.jpg", { type: "image/jpeg" });

      const formData = new FormData();

      formData.append("avatar", file);

      AdminChildrenService.changeAvatar(child.id, formData).then(() => {
        setIsLoading(false);
        setChild((prev) => (prev ? { ...prev, isActive: 0 } : null));
      });
    }, "image/jpeg");
  }

  async function onImageAdd(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();

    if (!requireLegalConsent()) {
      e.currentTarget.value = "";
      return;
    }

    const files = (e.currentTarget as any).files;
    const file = files[0];

    const isImageValid = validateImage(file);

    if (!isImageValid || !child) {
      e.currentTarget.value = "";
      return;
    }

    setIsLoading(true);

    const formData = new FormData();

    const correctImage = (await resizeImage(file, 1024)) as any;

    formData.append("image", correctImage, "image.jpg");

    const res = await AdminChildrenService.addImage(child.id, formData);
    if (res.status === "success") {
      getData();
    }
  }

  useEffect(() => {
    if (canEditChild) {
      onAvatarResize();
    }
    setIsLoading(false);
  }, [scale]);

  async function onImageDelete(imageId: number | string) {
    if (!child) return;
    if (!requireLegalConsent()) return;

    setIsLoading(true);

    const res = await AdminChildrenService.deleteImage(child.id, imageId);
    if (res.status === "success") {
      getData();
    }
  }

  const isFirst = useRef(true);
  useEffect(() => {
    if (!child) return;
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    if (!isLegalAccepted) return;

    AdminChildrenService.updateChild(child.id, {
      name,
      surname,
      specialization,
      sex,
      birthDate,
      height,
      hairColor,
      eyeColor,
      shoesSize,
      city,
      video,
      secondVideo,
    })
      .then(getData)
      .catch(console.log);
  }, [name, surname, specialization, sex, birthDate, height, hairColor, eyeColor, shoesSize, city, video, secondVideo]);

  async function publishChild() {
    if (!child) return;
    if (!requireLegalConsent()) return;

    const res = await AdminChildrenService.publishChild(child.id);
    if (res.status === "error") {
      setIsError(true);
      setTimeout(setIsError, 5000, false);
      return;
    }

    getData();
  }

  async function acceptLegalConsent() {
    if (!child) return;

    if (!offerAccepted || !privacyAccepted || !parentConsentAccepted) {
      setLegalError(LEGAL_ERROR);
      return;
    }

    setIsLoading(true);
    setLegalError("");

    const res = await AdminChildrenService.acceptLegalConsent(child.id);
    if (res.status === "success") {
      await getData();
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setLegalError(res?.data?.message || LEGAL_ERROR);
  }

  function onBackClick() {
    if (!user) {
      return;
    }

    if (user.isAdmin) {
      navigate("/admin/children");
      return;
    }

    AuthService.removeAuth();
    navigate("/admin/login");
  }

  return (
    <AdminChildBlock>
      <Container>
        <AdminChildContent>
          <AdminChildInfo data-disabled={!canEditChild}>
            {child && !isLegalAccepted && (
              <AdminChildLegalBlock>
                <AdminChildLegalTitle>Согласия перед заполнением анкеты</AdminChildLegalTitle>
                <AdminChildLegalText>Перед сохранением данных ребёнка подтвердите юридические согласия. Без этого анкета не будет сохраняться.</AdminChildLegalText>

                <AdminChildLegalCheckbox>
                  <AdminChildLegalCheckboxInput type="checkbox" checked={offerAccepted} onChange={(e) => setOfferAccepted(e.currentTarget.checked)} />
                  <span>
                    Я принимаю условия <AdminChildLegalLink href="/offer" target="_blank" rel="noreferrer">Публичной оферты</AdminChildLegalLink>.
                  </span>
                </AdminChildLegalCheckbox>

                <AdminChildLegalCheckbox>
                  <AdminChildLegalCheckboxInput type="checkbox" checked={privacyAccepted} onChange={(e) => setPrivacyAccepted(e.currentTarget.checked)} />
                  <span>
                    Я даю согласие на обработку персональных данных в соответствии с <AdminChildLegalLink href="/privacy" target="_blank" rel="noreferrer">Политикой обработки персональных данных</AdminChildLegalLink>.
                  </span>
                </AdminChildLegalCheckbox>

                <AdminChildLegalCheckbox>
                  <AdminChildLegalCheckboxInput type="checkbox" checked={parentConsentAccepted} onChange={(e) => setParentConsentAccepted(e.currentTarget.checked)} />
                  <span>
                    Я подтверждаю, что являюсь родителем или законным представителем ребёнка и даю <AdminChildLegalLink href="/parent-consent" target="_blank" rel="noreferrer">Согласие родителя</AdminChildLegalLink> на обработку, хранение и использование данных ребёнка для размещения анкеты и участия в кастинг-предложениях.
                  </span>
                </AdminChildLegalCheckbox>

                <AdminChildLegalButton type="button" onClick={acceptLegalConsent} disabled={!offerAccepted || !privacyAccepted || !parentConsentAccepted}>
                  Подтвердить и продолжить
                </AdminChildLegalButton>

                {legalError && <AdminChildError>{legalError}</AdminChildError>}
              </AdminChildLegalBlock>
            )}
            <AdminChildBackButton onClick={onBackClick}>{user?.isAdmin ? "Назад" : "Выйти"}</AdminChildBackButton>

            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Имя</AdminChildInfoContentItemText>
              <AdminChildInfoContentItemInput value={name} onChange={(e) => setName(e.currentTarget.value)} placeholder="Введите имя" />
            </AdminChildInfoContentItem>
            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Фамилия</AdminChildInfoContentItemText>
              <AdminChildInfoContentItemInput value={surname} onChange={(e) => setSurname(e.currentTarget.value)} placeholder="Введите фамилию" />
            </AdminChildInfoContentItem>
            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Направление</AdminChildInfoContentItemText>
              <AdminChildSelect value={specialization} values={["Модели", "Актеры", "Модели/Актеры"]} onChange={setSpecialization} placeholder="Выберите направление" />
            </AdminChildInfoContentItem>
            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Пол</AdminChildInfoContentItemText>
              <AdminChildSelect value={sex} values={["Женский", "Мужской"]} onChange={setSex} placeholder="Выберите пол ребенка" />
            </AdminChildInfoContentItem>
            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Дата рождения</AdminChildInfoContentItemText>
              <AdminChildInfoContentItemDatePicker
                value={birthDate}
                onChange={(date) => setBirthDate(moment(date).format(`YYYY-MM-DD`))}
                placeholderText="Укажите дату рождения ребенка"
                dateFormatCalendar=" "
                showMonthDropdown
                showYearDropdown
                yearDropdownItemNumber={24}
                openToDate={birthDate ? moment(birthDate).toDate() : moment().toDate()}
                maxDate={new Date(Date.now())}
                minDate={new Date("2002/01/1")}
              />
            </AdminChildInfoContentItem>
            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Рост</AdminChildInfoContentItemText>
              <AdminChildInfoContentItemInput value={height} onChange={(e) => setHeight(e.currentTarget.value)} placeholder="Укажите рост ребенка " />
            </AdminChildInfoContentItem>
            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Цвет волос</AdminChildInfoContentItemText>
              <AdminChildSelect value={hairColor} values={["Блондин", "Брюнет", "Разноцветный", "Русый", "Рыжий", "Седой", "Шатен"]} onChange={setHairColor} placeholder="Выберите цвет волос" />
            </AdminChildInfoContentItem>
            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Цвет глаз</AdminChildInfoContentItemText>
              <AdminChildSelect
                value={eyeColor}
                values={["Буро-желто-зеленые", "Голубые", "Желтые", "Зеленые", "Светло-карие", "Светлые с буро-желтым венчиком", "Серо-голубые", "Серо-зеленые", "Серые", "Синие", "Темно-карие", "Черные"]}
                onChange={setEyeColor}
                placeholder="Выберите цвет волос"
              />
            </AdminChildInfoContentItem>
            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Размер обуви</AdminChildInfoContentItemText>
              <AdminChildInfoContentItemInput value={shoesSize} onChange={(e) => setShoesSize(e.currentTarget.value)} placeholder="Укажите размер обуви" />
            </AdminChildInfoContentItem>
            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Город</AdminChildInfoContentItemText>
              <AdminChildSelect value={city} values={["Москва", "Санкт-Петербург", "Москва/Санкт-Петербург"]} onChange={setCity} placeholder="Выберите город" />
            </AdminChildInfoContentItem>
            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Видиовизитка актера</AdminChildInfoContentItemText>
              <AdminChildInfoContentItemInput value={video} onChange={(e) => setVideo(e.currentTarget.value)} placeholder="Вставьте ссылку из youtube/VK" />
            </AdminChildInfoContentItem>

            <AdminChildInfoContentItem>
              <AdminChildInfoContentItemText>Видиовизитка модели</AdminChildInfoContentItemText>
              <AdminChildInfoContentItemInput value={secondVideo} onChange={(e) => setSecondVideo(e.currentTarget.value)} placeholder="Вставьте ссылку из youtube/VK" />
            </AdminChildInfoContentItem>
          </AdminChildInfo>
          <AdminChildAvatar>
            <AdminChildAvatarTitle>Аватар</AdminChildAvatarTitle>
            {child?.avatar ? (
              <>
                <AdminChildAvatarEditor ref={editor} image={child.avatar} scale={scale} border={100} onMouseUp={onAvatarResize} crossOrigin="anonymous" width={1024} height={1024} />
                <AdminChildAvatarScale type="range" min={1} max={2} step={0.01} value={scale} onChange={(e) => setScale(Number(e.currentTarget.value))} />
              </>
            ) : (
              <AdminChildAvatarImage src={child?.avatar ?? ""} onChange={onAvatarChange} disabled={!canEditChild} />
            )}
            {child?.avatar && <AdminChildAvatarButton onChange={onAvatarChange}>Заменить</AdminChildAvatarButton>}
          </AdminChildAvatar>
        </AdminChildContent>
        <AdminChildImages>
          <AdminChildImagesTitle>Портфолио</AdminChildImagesTitle>
          <AdminChildImagesBody>
            {child?.images?.map((image) => {
              return (
                <AdminChildImageBlock key={image.id}>
                  <AdminChildImageDelete onClick={() => onImageDelete(image.id)} src="/images/delete.svg" />
                  <AdminChildImage src={image.url} />
                </AdminChildImageBlock>
              );
            })}

            {(child?.images?.length ?? 12) < 12 && <AdminChildImageAdd onChange={onImageAdd} disabled={!canEditChild}></AdminChildImageAdd>}
          </AdminChildImagesBody>
        </AdminChildImages>

        <AdminChildButtons>
          <AdminChildButton isActive={!!!child?.isActive} onClick={publishChild} disabled={!canEditChild}>
            {!!child?.isActive ? "Скрыть" : "Опубликовать"}
          </AdminChildButton>
        </AdminChildButtons>

        {isLoading && (
          <AdminChildLoading>
            <AdminChildLoadingCircle />
          </AdminChildLoading>
        )}

        {isError && <AdminChildError>Ошибка. Вы не заполнили все поля. 3 или более фото, аватар и все поля должны быть заполнены</AdminChildError>}
      </Container>
    </AdminChildBlock>
  );
};

export default AdminChild;
